import { Dialog } from "@headlessui/react";
import { useState } from "react";
import { getName, stringFormattingToCurrency } from "js/formatting";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Badge from "components/General/Badge";
import { useEffect } from "react";
import { getAgent } from "redux/agent/slices/getAgentSlice";
import { useDispatch } from "react-redux";
import LoadingOverlay from "react-loading-overlay";
import {
  EverlakeLoadingSpinner,
  EverlakeLoadingOverlayStyle,
} from "components/General/EverlakeLoading";
import { canShowEarnings } from "js/agentCard-util";
import { useTranslation, Trans } from "react-i18next";
import { getImage } from "js/utilities";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

export default function AgentCard({ data, manager }) {
  LoadingOverlay.propTypes = undefined;
  let dispatch = useDispatch();
  const [isOpenAgentProfile, setIsOpenAgentProfile] = useState(false);
  const [policyList, setPolicyList] = useState(false);
  const { agentPolicies } = useContext(AgentData);
  const { user } = useSelector((s) => s.user);
  const [agentInfo, setAgentInfo] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [agentData, setAgentData] = useState();
  const { t } = useTranslation();
  let policyIds = user?.agentHierarchy?.find(
    (x) => x.agentId === data?.agentId
  )?.policyIds;
  const getAgentInfoApi = (agentId) => {
    if (agentId) {
      setIsLoading(true);
      dispatch(getAgent({ agentId: agentId, updateStore: false }))
        .then((response) => {
          if (response?.meta?.requestStatus === "fulfilled") {
            setAgentInfo(response?.payload);
            setIsOpenAgentProfile(true);
          } else {
            console.error("Something wrong.", response?.payload?.status);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("Something wrong.", error);
        });
    }
  };

  useEffect(() => {
    if (policyIds?.length > 0) {
      let policyList = [];
      if (agentPolicies?.length > 0) {
        for (let i = 0; i < agentPolicies?.length; i++) {
          let contractID = policyIds.find(
            (id) => id === agentPolicies[i].contractID
          );
          if (contractID) {
            policyList.push(agentPolicies[i]);
          }
        }
        setPolicyList(policyList);
      }
    }
  }, [agentPolicies, policyIds]);

  useEffect(() => {
    setAgentData({
      agentId: data?.agentId,
      agentName: getName(data?.agentInfo?.parties?.person),
      pronouns: "",
      photoURL: "",
      badgeStatus: agentInfo?.status === "A" ? "good" : "",
      badgeText: agentInfo?.status === "A" ? "Active / Compliant" : "",
      earnings:
        (user?.role?.roleName?.toLowerCase() === "agent" ||
          (user?.role?.roleName?.toLowerCase() === "agent assistant" &&
            user?.viewCommissions === 1)) &&
        data.isMasterAgent === true
          ? `$${data?.commissionAmount}`
          : "",
      profileURL: "/",
      bookURL: "/",
      office: "",
      address:
        agentInfo?.addresses?.find((x) => x.type === "BUSN")?.addressLine1 ??
        "",
      address2:
        agentInfo?.addresses?.find((x) => x.type === "BUSN")?.addressLine2 ??
        "",
      city:
        agentInfo?.addresses?.find((x) => x.type === "BUSN")?.addressLine2 ??
        "",
      state: agentInfo?.addresses?.find((x) => x.type === "BUSN")?.state ?? "",
      zip: agentInfo?.addresses?.find((x) => x.type === "BUSN")?.zip ?? "",
      busnPhone:
        agentInfo?.phones?.find((x) => x.type === "BUSN")?.phoneNumber ?? "",
      homePhone:
        agentInfo?.phones?.find((x) => x.type === "HOME")?.phoneNumber ?? "",
    });
  }, [agentInfo, user]);

  return (
    <>
      <LoadingOverlay
        active={isLoading}
        spinner={<EverlakeLoadingSpinner />}
        styles={EverlakeLoadingOverlayStyle}
      >
        <div className="ui-card ui-grid-item relative p-4">
          {/* Note: Need to ask where this arrow goes and updated the aria-label  */}
          <Link
            className="ui-btn-arrow-details hidden md:block md:top-[1.5rem] md:right-[.5rem]"
            aria-label={t("AgentCard-link-aria-label-1")}
            to={`/book-of-business?type=all&agentId=${agentData?.agentId}`}
          >
            <svg
              className="ui-icon w-8 h-8"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-next"></use>
            </svg>
          </Link>

          <div className="font-semibold">
            {t("AgentCard-agentName", {
              name: agentData?.agentName,
            })}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-[1fr,2fr] md:grid-cols-[1.5fr,2fr]">
            <div className="rounded overflow-hidden">
              <img
                src={`${
                  agentData?.photoURL?.length > 0
                    ? agentData?.photoURL
                    : getImage("image11.png")
                }`}
                className="w-full h-auto max-w-[10rem]"
                alt={t("AgentCard-agentImageAlt", {
                  name: agentData?.agentName,
                })}
              />
            </div>
            <div>
              <div>
                <div className="text-xl text-theme-text-secondary font-semibold">
                  {t("AgentCard-policyListLength", {
                    length: policyList?.length ?? "0",
                  })}
                </div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentCard-policyManagedLabel")}
                </div>
              </div>
              {canShowEarnings(user) && (
                <div className="mt-1">
                  <div className="text-xl text-theme-text-secondary font-semibold">
                    {t("AgentCard-commissionAmount", {
                      price: stringFormattingToCurrency(data?.commissionAmount),
                    })}
                  </div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AgentCard-commissionAmountLabel")}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-1 items-stretch">
            <button
              className="ui-btn-secondary-sm mr-2 sm:w-full"
              onClick={() => getAgentInfoApi(agentData?.agentId)}
            >
              {t("AgentCard-ViewProfile-button-text")}
            </button>
            {/* <button className="ui-btn-primary-sm sm:w-full">View Book</button> */}
            <Link
              className="ui-btn-primary-sm sm:w-full"
              aria-label={t("AgentCard-ViewBook-button-aria-label", {
                name: agentData?.agentName,
              })}
              to={`/book-of-business?type=all&agentId=${agentData?.agentId}`}
            >
              {t("AgentCard-ViewBook-link-text")}
            </Link>
          </div>
        </div>
      </LoadingOverlay>
      {/* Agent Profile Dialog */}
      <Dialog
        open={isOpenAgentProfile}
        onClose={() => setIsOpenAgentProfile(false)}
        className="ui-dialogs"
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        {/* Full-screen container to center the panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog"
          >
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-[1fr,3fr]">
              <div>
                <img
                  src={`${
                    agentData?.photoURL?.length > 0
                      ? agentData?.photoURL
                      : getImage("image11.png")
                  }`}
                  className="w-full h-auto max-w-[14rem]"
                  alt={t("AgentCard-agentImageAlt", {
                    name: agentData?.agentName,
                  })}
                />
              </div>
              <div>
                <Dialog.Title className="font-body text-xl font-bold text-left">
                  {t("AgentCard-agentName", {
                    name: agentData?.agentName,
                  })}
                  {agentData?.pronouns && (
                    <span className="font-normal text-base ml-2">
                      {t("AgentCard-agentPronouns", {
                        pronouns: agentData?.pronouns,
                      })}
                    </span>
                  )}
                </Dialog.Title>
                <button
                  className="ui-default-dialog-close"
                  aria-label={t("AgentCard-closeDialog-aria-label")}
                  onClick={() => setIsOpenAgentProfile(false)}
                >
                  <svg
                    className="ui-icon h-3.5 w-3.5"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-close"></use>
                  </svg>
                </button>

                {agentData?.badgeStatus && (
                  <Badge
                    status={agentData?.badgeStatus}
                    statusText={t("AgentCard-Badge-status-text", {
                      text: agentData?.badgeText,
                    })}
                    srPrefix={t("AgentCard-Badge-sr-prefix")}
                  />
                )}
                <div>
                  <hr />
                </div>
                {/* <Dialog.Description>
                  Space for copy for Rob to customize a brief bio about himself.
                  This would be displayed for managers and/or other users who
                  can see agent bios. This would be displayed for managers
                  and/or other users who can see agent bios.
                </Dialog.Description> */}
              </div>
            </div>

            <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentCard-dialog-homeOfficeLabel")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  <div>
                    {t("AgentCard-dialog-office", {
                      office: agentData?.office,
                    })}
                  </div>
                  <div>
                    {t("AgentCard-dialog-address", {
                      address: agentData?.address,
                    })}
                  </div>
                  <div>
                    {t("AgentCard-dialog-address2", {
                      address2: agentData?.address2,
                    })}
                  </div>
                  <div>
                    {agentData?.city
                      ? `${t("AgentCard-dialog-city", {
                          city: agentData?.city,
                        })}, `
                      : ""}
                    {t("AgentCard-dialog-state", { state: agentData?.state })}
                    {t("AgentCard-dialog-zip", { zip: agentData?.zip })}
                  </div>
                </div>
              </div>
              {/* <div>
                <div className="text-xs text-theme-text-tertiary">
                  Specialty
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  Whole Life and annuities
                </div>
              </div> */}
              {/* <div>
                <div className="text-xs text-theme-text-tertiary">
                  Fax Number{" "}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  000-0000-0000
                </div>
              </div> */}
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentCard-dialog-businessNumber-title")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {t("AgentCard-dialog-businessNumber-description", {
                    part1: agentData?.busnPhone?.substring(0, 3),
                    part2: agentData?.busnPhone?.substring(3, 6),
                    part3: agentData?.busnPhone?.substring(6),
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentCard-dialog-mobileNumber-title")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {t("AgentCard-dialog-mobileNumber-description", {
                    part1: agentData?.homePhone?.substring(0, 3),
                    part2: agentData?.homePhone?.substring(3, 6),
                    part3: agentData?.homePhone?.substring(6),
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AgentCard-dialog-managerLabel")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {manager
                    ? t("AgentCard-dialog-managerName", { name: manager })
                    : ""}
                </div>
              </div>
            </div>
            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                onClick={() => setIsOpenAgentProfile(false)}
              >
                <Trans
                  i18nKey="AgentCard-dialog-closeButton"
                  components={{
                    span1: <span className="sr-only" />,
                  }}
                />
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
